import React, { useState, useEffect } from 'react';
import { Database, TestTube2, Users, Check, ChevronsUpDown, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HelpDrawer } from '@/components/ProductTour';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { Breadcrumbs } from './Breadcrumbs';

import { useTimesheetForScreen } from '@/contexts/TimesheetContext';
import { useDataSource } from '@/contexts/DataSourceContext';
import { getScreenIdFromPath, getScreenNameFromId, isSettingsPath } from '@/utils/screenMapping';
import { usePersona } from '@/contexts/PersonaContext';

interface HeaderProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

export const Header: React.FC<HeaderProps> = ({ currentPath, onNavigate }) => {
  const { getScreenMode, getAllScreenModes, isModeLocked } = useDataSource();
  const { currentPersona } = usePersona();

  // Derive a composite help key so the Manager View gets its own content
  // even though it shares the '/' route with the Employee homepage
  const helpKey = currentPath === '/' && currentPersona === 'manager'
    ? '/manager'
    : currentPath;
  
  const currentScreenId = getScreenIdFromPath(currentPath);
  const currentScreenName = currentScreenId ? getScreenNameFromId(currentScreenId) : null;
  const isSettings = isSettingsPath(currentPath);
  
  // Use the screen ID from path mapping (works for both regular screens and settings pages)
  // Use getScreenMode for all paths - it falls back to getDefaultMode() for unknown screen IDs
  const currentMode = getScreenMode(currentScreenId || 'default');
  const isDemo = currentMode === 'demo';
  
  // Use the current screen's context for proper Live/Demo mode handling
  // This ensures the Header always shows the correct user based on the current screen's mode
  // We use 'dashboard' as the default screenId for unknown screens to ensure consistent behavior
  const effectiveScreenId = currentScreenId || 'dashboard';
  const screenContext = useTimesheetForScreen(effectiveScreenId, true);
  
  const { currentUser, employees, switchEmployee } = screenContext;
  const [open, setOpen] = React.useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  // Global keyboard shortcut: Press "?" to open/close the Help drawer
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInputField =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable;
      if (e.key === '?' && !isInputField && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        setHelpOpen(prev => !prev);
      }
    };
    window.addEventListener('keypress', handleKeyPress);
    return () => window.removeEventListener('keypress', handleKeyPress);
  }, []);
  
  // Safely derive loading for per-screen contexts (only useTimesheetForScreen returns loading)
  const loading = 'loading' in screenContext ? screenContext.loading : false;
  
  // Check if we're loading employees in live mode
  const isLoadingEmployees = loading && employees.length === 0;
  
  // Calculate screen mode statistics
  const allScreens = getAllScreenModes();
  const stats = {
    total: allScreens.length,
    demo: allScreens.filter(s => s.mode === 'demo').length,
    live: allScreens.filter(s => s.mode === 'live').length,
  };

  return (
    <header className="sticky top-0 z-30 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-12 sm:h-14 lg:h-16 items-center gap-2 sm:gap-3 lg:gap-4 px-3">
        {/* Breadcrumbs - Hidden on mobile */}
        <div className="flex-1 hidden md:block">
          <Breadcrumbs currentPath={currentPath} onNavigate={onNavigate} />
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Employee Selector with Search - Only shown in Demo mode, hidden on Screen Data Modes page */}
          {isDemo && !currentPath.startsWith('/settings/screens') && (
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  className="w-[200px] justify-between hidden lg:flex"
                  disabled={isLoadingEmployees}
                >
                  <Users className="h-4 w-4 mr-2 shrink-0" />
                  <span className="truncate">
                    {isLoadingEmployees ? "Loading..." : (currentUser ? currentUser.name : "Select employee")}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[250px] p-0">
                <Command>
                  <CommandInput placeholder="Search employees..." />
                  <CommandList>
                    {isLoadingEmployees ? (
                      <div className="py-6 text-center text-sm text-muted-foreground">
                        Loading employees...
                      </div>
                    ) : (
                      <>
                        <CommandEmpty>No employee found.</CommandEmpty>
                        <CommandGroup>
                          {employees.map((employee) => (
                            <CommandItem
                              key={employee.id}
                              value={`${employee.name} ${employee.role}`}
                              onSelect={() => {
                                switchEmployee(employee.id);
                                setOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  currentUser?.id === employee.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{employee.name}</span>
                                <span className="text-xs text-muted-foreground">{employee.role}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}

          {/* Subtle Mode Indicator with Tooltip — hidden when the mode cannot be changed */}
          {!isModeLocked && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => onNavigate('/settings/screens')}
                  className="hidden sm:inline-flex"
                >
                  <span className="text-xs text-muted-foreground/70 font-medium tracking-wide uppercase cursor-pointer hover:text-muted-foreground transition-colors">
                    {isDemo ? 'Demo' : 'Real Data'}
                  </span>
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-md">
                <div className="space-y-3">
                  {/* Current Screen Mode */}
                  {currentScreenName && !isSettings ? (
                    <>
                      <div>
                        <p className="font-bold text-sm mb-1">
                          {isDemo ? '🎭 Demo Mode' : '🔌 Real Data Mode'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {isDemo 
                            ? 'Viewing sample data for testing. No real data is affected.'
                            : 'Connected to production database. All changes affect real data.'
                          }
                        </p>
                      </div>
                      
                      {/* Current Screen Info */}
                      <div className="pt-2 border-t">
                        <p className="text-xs font-semibold mb-1">Current Screen:</p>
                        <p className="text-xs text-muted-foreground">{currentScreenName}</p>
                      </div>
                    </>
                  ) : (
                    <div>
                      <p className="font-bold text-sm mb-1">
                        {isSettings ? '⚙️ Settings' : '📊 Application'}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {isSettings 
                          ? 'Configure screen modes and application settings.'
                          : 'Navigate to a screen to see its mode status.'
                        }
                      </p>
                    </div>
                  )}
                  
                  {/* Screen Statistics */}
                  <div className="space-y-1 text-xs pt-2 border-t">
                    <p className="font-semibold mb-1">All Screens:</p>
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-1">
                        <TestTube2 className="h-3 w-3 text-amber-600" />
                        Demo Mode:
                      </span>
                      <span className="font-medium">{stats.demo} screens</span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="flex items-center gap-1">
                        <Database className="h-3 w-3 text-green-600" />
                        Real Data:
                      </span>
                      <span className="font-medium">{stats.live} screens</span>
                    </div>
                  </div>
                  
                  {/* Action */}
                  <p className="text-xs text-muted-foreground pt-2 border-t">
                    💡 Click to configure screen modes
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          )}

          {/* Help Button */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 h-8 text-xs"
                  onClick={() => setHelpOpen(true)}
                  data-tour="help-menu"
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                  Help
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                Get help with this page
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

        </div>
      </div>

      {/* Help Drawer */}
      <HelpDrawer
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        currentPath={helpKey}
        onNavigate={onNavigate}
        onReopenAfterNavigate={() => setHelpOpen(true)}
      />
    </header>
  );
};
